import BottomSheet from "../../Common/BottomSheet";
import { lg } from "@/utils/generalUtils";
import Image from "next/image";
import getRandomPfp from "@/utils/getRandomPfp";
import { useAppSelector } from "@/redux/hooks";
import { useEffect, useState } from "react";
import { getGCCHoldings } from "@/services/getGCCHoldings";
import { IGCCHoldings } from "@/types/IGCCHoldings";
import useSolanaTransaction from "@/callbacks/useSolanaTransaction";
import GradientTick from "@/assetsV3/GradientTick";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import SearchIcon from "@/assetsV3/SearchIcon";
import SearchClose from "@/assetsV3/Wallet/SearchClose";
import useDebounce from "@/hooks/useDebounce";

interface Props {
  gccSheetState: boolean;
  setSelectedGcc: (val: IGCCHoldings) => void;
  selectedGcc: IGCCHoldings | undefined;
  handleGccSheetChange: (isOpen: boolean) => void;
}

const GccSelectionSheet: React.FC<Props> = ({
  handleGccSheetChange,
  selectedGcc,
  setSelectedGcc,
  gccSheetState,
}) => {
  const [holdings, setHoldings] = useState<IGCCHoldings[]>([]);
  const user = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { fromLamports } = useSolanaTransaction();
  const _currentWallet = useAppSelector((state) => state.wallet);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearch = useDebounce(searchText, 500);

  const getData = async () => {
    setIsLoading(true); // Start loader
    try {
      const gccHoldings = await getGCCHoldings(0, _currentWallet.address, debouncedSearch || "");
      setHoldings(gccHoldings.filter(holding => holding.tokens !== holding.tokens_onmarket));
    } catch (error) {
      console.error("Error fetching data:", error); // Handle errors here
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  useEffect(() => {
    user && _currentWallet && _currentWallet.address !== "" && getData();
  }, [user, _currentWallet]);

  useEffect(() => {
    setIsLoading(true)
    getData()
  }, [debouncedSearch])

  return (
    <>
      {window.innerWidth < lg ? (
        <BottomSheet classsName='!pb-0' isOpen={gccSheetState} onChange={handleGccSheetChange}>

          <div className="overflow-hidden max-h-[40vh]">
            <div className="flex flex-col gap-4">
              <div
                id="container"
                className="w-full outmd:mx-0 border flex flex-row gap-2 justify-center items-center px-3 py-2 min-h-[48px] border-outstate-grayborder rounded-3xl  transition duration-300 ease-in-out group cursor-pointer container"
              >
                <SearchIcon width={24} height={24} className="fill-outext-sub stroke-outext-sub" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchText}
                  className="placeholder:text-outext-sub text-outext-main outline-none font-medium text-base overflow-hidden flex-1 h-full bg-transparent"
                  onChange={(e) => { setSearchText(e.target.value) }}
                />
                {
                  debouncedSearch && debouncedSearch.length > 0
                    ?
                    <span onClick={() => { setSearchText(""); }}>
                      <SearchClose />
                    </span>
                    :
                    <></>
                }
              </div>
            </div>
            <div className="flex flex-col gap-3 overflow-y-scroll pt-[20px] min-h-[50px] max-h-[300px] no-scrollbar pb-[30px]">
              {isLoading ? (
                [1, 2, 3].map((_, index) => (
                  <div key={index} className={`flex w-full rounded-lg  py-3 ${index === 0 ? 'pt-3' : ''}`}>
                    <SkeletonTheme
                      key={index}
                      baseColor="#373737"
                      highlightColor="#C0C0C0"
                    >
                      <div
                        key={index}
                        className={`px-3 rounded-lg  flex justify-between items-center gap-5 cursor-pointer `}
                      >
                        <Skeleton
                          count={1}
                          width={"48px"}
                          height={"48px"}
                          style={{ borderRadius: "100%" }}
                        />
                        <div className="flex flex-1 flex-col text-base gap-1">
                          <div className="flex gap-1 items-center">
                            <span className="font-bold text-outext-main text-base">
                              <Skeleton
                                count={1}
                                width={"100px"}
                                height={"20px"}
                                style={{ borderRadius: "8px" }}
                              />
                            </span>
                          </div>
                          <p className="text-outext-disabled font-normal">
                            <Skeleton
                              count={1}
                              width={"100px"}
                              height={"20px"}
                              style={{ borderRadius: "8px" }}
                            />
                          </p>
                        </div>
                      </div>
                    </SkeletonTheme>

                  </div>
                ))
              ) : holdings.length > 0 ? (
                holdings.map((holding, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      handleGccSheetChange(false);
                      setSelectedGcc(holding);
                    }}
                    className={`px-3 py-3 rounded-lg flex justify-between items-center gap-[18px] cursor-pointer ${selectedGcc?.subject.outId === holding.subject.outId
                      ? "bg-outgray-graylighter"
                      : ""
                      } ${index === 0 ? 'pt-3' : ''}`}
                  >
                    <div className="flex flex-row  w-full items-center gap-[18px] group-hover:ml-1 transition-margin duration-300 text-nowrap">
                      <Image
                        height={48}
                        width={48}
                        src={
                          holding.subject.profilePic
                            ? holding.subject.profilePic
                            : getRandomPfp()
                        }
                        alt="test"
                        className="rounded-full h-[48px] w-[48px]"
                        onError={async ({ currentTarget }) => {
                          currentTarget.onerror = null; // prevents looping
                          currentTarget.src = getRandomPfp();
                        }}
                      />
                      <div className="grid grid-flow-row mr-1">
                        <div className="flex flex-1 flex-col gap-1  text-clip overflow-hidden text-nowrap justify-start items-start">
                          <span
                            id="displayNameVouch"
                            className="text-base text-outext-main leading-5 "
                          >
                            {holding.subject.displayName}
                          </span>
                          <span className="text-base text-outext-sub font-medium ">
                            @{holding.subject.handle}
                          </span>
                        </div>
                      </div>

                    </div>
                    <div className="flex justify-end items-center gap-1">
                      <span className="text-base text-outext-disabled leading-4 whitespace-nowrap">
                        {fromLamports(holding.tokens) -
                          fromLamports(holding.tokens_onmarket)}{" "}
                        {fromLamports(holding.tokens) -
                          fromLamports(holding.tokens_onmarket) >
                          1
                          ? "Chips"
                          : "Chip"}
                      </span>
                      {selectedGcc?.subject.outId === holding.subject.outId ? (
                        <div className="flex justify-center items-center h-6 w-6">
                          <GradientTick />
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center justify-center mt-6">
                  <p className="text-base text-outext-sub">No Data</p>
                </div>
              )}
            </div>

          </div>
        </BottomSheet >
      ) : (
        <></>
      )}
    </>
  );
};

export default GccSelectionSheet;
